import axios from "axios";
import { clearToken, getToken } from "@/lib/auth";
import type {
  CategoryCountsResponse,
  Product,
  ProductCountResponse,
  ProductCreate,
  ProductListParams,
  ProductListResponse,
  ProductUpdate,
} from "@/types/product";
import type { CategoryImage, HeroSlide, UploadFolder } from "@/types/site";

/** Empty REACT_APP_BACKEND_URL = same-origin /api (Docker/nginx production). */
function apiBaseUrl(): string {
  const origin = process.env.REACT_APP_BACKEND_URL;
  if (origin === "") return "/api";
  const base = (origin || "http://localhost:8000").replace(/\/$/, "");
  return `${base}/api`;
}

export const api = axios.create({
  baseURL: apiBaseUrl(),
  headers: { "Content-Type": "application/json" },
  timeout: 15_000,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && getToken()) {
      clearToken();
      if (
        typeof window !== "undefined" &&
        window.location.pathname.startsWith("/admin") &&
        !window.location.pathname.includes("/login")
      ) {
        window.location.assign("/admin/login");
      }
    }
    return Promise.reject(error);
  },
);

export const fetchProductList = async (
  params: ProductListParams = {},
): Promise<ProductListResponse> => {
  const { data } = await api.get<ProductListResponse>("/products", { params });
  return data;
};

export const fetchProductCount = async (
  params: Pick<ProductListParams, "category" | "q"> = {},
): Promise<number> => {
  const { data } = await api.get<ProductCountResponse>("/products/count", { params });
  return data.total;
};

export const fetchCategoryProductCounts = async (): Promise<CategoryCountsResponse> => {
  const { data } = await api.get<CategoryCountsResponse>("/products/category-counts");
  return data;
};

export const fetchRelatedProducts = async (
  id: string,
  limit = 4,
): Promise<Product[]> => {
  const { data } = await api.get<Product[]>(`/products/${encodeURIComponent(id)}/related`, {
    params: { limit },
  });
  return data;
};

export const fetchProduct = async (id: string): Promise<Product> => {
  const { data } = await api.get<Product>(`/products/${encodeURIComponent(id)}`);
  return data;
};

export const createProduct = async (product: ProductCreate): Promise<Product> => {
  const { data } = await api.post<Product>("/products", product);
  return data;
};

export const updateProduct = async (id: string, product: ProductUpdate): Promise<Product> => {
  const { data } = await api.put<Product>(`/products/${encodeURIComponent(id)}`, product);
  return data;
};

export const deleteProduct = async (id: string): Promise<void> => {
  await api.delete(`/products/${encodeURIComponent(id)}`);
};

export const loginAdmin = async (
  username: string,
  password: string,
): Promise<{ access_token: string; token_type: string }> => {
  const { data } = await api.post<{ access_token: string; token_type: string }>("/auth/login", {
    username,
    password,
  });
  return data;
};

export type AdminProfile = {
  username: string;
};

export type AdminSettingsPayload = {
  current_password: string;
  username?: string;
  new_password?: string;
};

export const fetchAdminProfile = async (): Promise<AdminProfile> => {
  const { data } = await api.get<AdminProfile>("/auth/me");
  return data;
};

export const updateAdminSettings = async (
  payload: AdminSettingsPayload,
): Promise<{ access_token: string; token_type: string }> => {
  const { data } = await api.put<{ access_token: string; token_type: string }>("/auth/settings", payload);
  return data;
};

export type ImageUploadResult = {
  url: string;
  public_id?: string;
  width?: number;
  height?: number;
  format?: string;
};

export type ImageUploadProgress = {
  loaded: number;
  total?: number;
};

export const uploadProductImage = async (
  file: File,
  onUploadProgress?: (progress: ImageUploadProgress) => void,
): Promise<ImageUploadResult> => {
  return uploadImage(file, "products", onUploadProgress);
};

export const uploadImage = async (
  file: File,
  folder: UploadFolder = "products",
  onUploadProgress?: (progress: ImageUploadProgress) => void,
): Promise<ImageUploadResult> => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<ImageUploadResult>("/uploads/image", formData, {
    params: { folder },
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60_000,
    onUploadProgress: onUploadProgress
      ? (event) => onUploadProgress({ loaded: event.loaded, total: event.total })
      : undefined,
  });
  return data;
};

export const fetchHeroSlides = async (): Promise<HeroSlide[]> => {
  const { data } = await api.get<{ slides: HeroSlide[] }>("/site/hero");
  return data.slides;
};

export const updateHeroSlides = async (slides: HeroSlide[]): Promise<HeroSlide[]> => {
  const { data } = await api.put<{ slides: HeroSlide[] }>("/site/hero", { slides });
  return data.slides;
};

export const fetchCategoryImages = async (): Promise<CategoryImage[]> => {
  const { data } = await api.get<CategoryImage[]>("/categories");
  return data;
};

export const updateCategoryImage = async (
  slug: string,
  url: string,
  publicId?: string | null,
): Promise<CategoryImage> => {
  const { data } = await api.put<CategoryImage>(`/categories/${slug}/image`, {
    url,
    public_id: publicId ?? null,
  });
  return data;
};

export type ContactMessagePayload = {
  name: string;
  email: string;
  message: string;
};

export const submitContactMessage = async (
  payload: ContactMessagePayload,
): Promise<{ ok: boolean }> => {
  const { data } = await api.post<{ ok: boolean }>("/contact", payload);
  return data;
};
